import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Brain, CheckCircle2, Crown, Lock, ScrollText } from "lucide-react";
import { refreshBasicHifz, goToPlans, goToApps } from "@/lib/hifzScope";
import { useLanguage } from "../i18n/LanguageContext";

/* Basic Hifz: what every account memorises for free, and why each piece is there (the hadith and where it is
   from). The plan's content is defined on the server (backend/basic_hifz.py) so the page, the daily plan and the
   limits can never disagree. */

const rangesLabel = (ranges) => ranges.map((r) => (r.from === r.to ? `${r.from}` : `${r.from}–${r.to}`)).join(", ");

function ItemCard({ it, t }) {
  const pct = it.total ? Math.round((it.memorized / it.total) * 100) : 0;
  const done = it.memorized >= it.total;
  const many = it.total > 1;
  return (
    <div className="hifz-card space-y-3" data-testid={`basic-item-${it.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            {it.surah_name_ar && <span className="ayah-arabic text-2xl text-foreground/85 shrink-0">{it.surah_name_ar}</span>}
            <div className="min-w-0">
              <div className="font-display font-semibold text-lg text-forest-700 truncate">{it.title}</div>
              <div className="text-xs text-muted-foreground">
                {t(many ? "basicHifz.ayahsMany" : "basicHifz.ayahOne", { ranges: rangesLabel(it.ranges) })}
              </div>
            </div>
          </div>
        </div>
        {it.next_ayah ? (
          <Link
            to={`/app/memorize/${it.surah}/${it.next_ayah}`}
            className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-forest-600 text-sand-100 text-sm font-medium hover:bg-forest-700 transition"
            data-testid={`basic-start-${it.id}`}
          >
            {it.memorized > 0 || it.learning > 0 ? t("basicHifz.continue") : t("basicHifz.start")} <ArrowRight className="h-4 w-4" />
          </Link>
        ) : (
          <span className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-forest-600/10 text-forest-700 text-xs font-semibold">
            <CheckCircle2 className="h-4 w-4" /> {t("basicHifz.memorised")}
          </span>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span>{t("basicHifz.progress", { done: it.memorized, total: it.total })}</span>
          <span>{pct}%</span>
        </div>
        <div className="h-2 bg-sand-200 rounded-full overflow-hidden">
          <div className={`h-full ${done ? "bg-gold-500" : "bg-forest-600"}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {it.why?.length > 0 && (
        <div className="rounded-xl bg-sand-200/60 border border-sand-300 px-3.5 py-3 space-y-2" data-testid={`basic-why-${it.id}`}>
          <div className="text-[10px] uppercase tracking-[0.18em] text-gold-700 font-semibold">{t("basicHifz.why")}</div>
          {it.why.map((w) => (
            <p key={w.source + w.text} className="text-sm text-foreground/85 leading-relaxed">
              {w.text} <span className="text-xs text-muted-foreground whitespace-nowrap">— {w.source}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function LadderStep({ st, t }) {
  const need = st.needs_course > 1 ? t("basicHifz.afterCoursesMany", { n: st.needs_course }) : t("basicHifz.afterCourses", { n: st.needs_course });
  return (
    <div className={`hifz-card flex items-start gap-3 ${st.open ? "" : "opacity-80"}`} data-testid={`ladder-step-${st.step}`}>
      <div className={`h-9 w-9 rounded-xl grid place-items-center shrink-0 font-display font-semibold ${st.open ? "bg-forest-600 text-sand-100" : "bg-sand-200 text-muted-foreground"}`}>{st.step}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display font-semibold text-forest-700">{st.name}</span>
          <span className={`status-pill ${st.open ? "status-memorized" : "status-not-started"}`} data-testid={`ladder-state-${st.step}`}>
            {st.open ? <CheckCircle2 className="h-3 w-3" /> : <Lock className="h-3 w-3" />} {st.open ? t("basicHifz.stepOpen") : t("basicHifz.stepLocked")}
          </span>
        </div>
        {st.detail && <p className="text-xs text-muted-foreground mt-0.5">{st.detail}</p>}
        <p className="text-xs text-foreground/70 mt-1">{st.access} · {need}</p>
      </div>
    </div>
  );
}

export default function BasicHifz() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // not the cached copy: progress made a moment ago in the memorise flow should show up here
    refreshBasicHifz().then(setData).catch(() => setFailed(true));
  }, []);

  if (failed) return <p className="text-sm text-terracotta-600" data-testid="basic-hifz-error">{t("basicHifz.loadFailed")}</p>;
  if (!data) return <div className="hifz-card h-64 bg-sand-200/50 animate-pulse-soft" data-testid="basic-hifz-skeleton" />;

  const daily = data.items.filter((i) => i.group === "daily");
  const last10 = data.items.filter((i) => i.group === "last10");

  return (
    <div className="space-y-8" data-testid="basic-hifz-page">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{t("basicHifz.eyebrow")}</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">{t("basicHifz.title")}</h1>
        <p className="mt-3 text-sm md:text-base text-foreground/75 max-w-2xl leading-relaxed">{t("basicHifz.intro")}</p>
        <div className="mt-4 max-w-md" data-testid="basic-hifz-total">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>{t("basicHifz.progress", { done: data.memorized, total: data.total })}</span>
            <span>{data.total ? Math.round((data.memorized / data.total) * 100) : 0}%</span>
          </div>
          <div className="h-2.5 bg-sand-200 rounded-full overflow-hidden">
            <div className="h-full bg-forest-600" style={{ width: `${data.total ? Math.round((data.memorized / data.total) * 100) : 0}%` }} />
          </div>
        </div>
      </header>

      <section className="space-y-3" data-testid="basic-group-daily">
        <h2 className="font-display font-semibold text-xl text-forest-700 flex items-center gap-2"><ScrollText className="h-5 w-5 text-gold-600" /> {t("basicHifz.groupDaily")}</h2>
        {daily.map((it) => <ItemCard key={it.id} it={it} t={t} />)}
      </section>

      <section className="space-y-3" data-testid="basic-group-last10">
        <h2 className="font-display font-semibold text-xl text-forest-700 flex items-center gap-2"><ScrollText className="h-5 w-5 text-gold-600" /> {t("basicHifz.groupLast10")}</h2>
        {last10.map((it) => <ItemCard key={it.id} it={it} t={t} />)}
      </section>

      <section className="space-y-3" data-testid="hifz-ladder">
        <h2 className="font-display font-semibold text-xl text-forest-700 flex items-center gap-2"><ScrollText className="h-5 w-5 text-gold-600" /> {t("basicHifz.stepsTitle")}</h2>
        {data.ladder.map((st) => <LadderStep key={st.step} st={st} t={t} />)}
        <button type="button" onClick={goToApps} className="text-sm font-medium text-forest-700 hover:underline inline-flex items-center gap-1.5" data-testid="ladder-open-courses">
          {t("basicHifz.openCourses")} <ArrowRight className="h-4 w-4" />
        </button>
      </section>

      <section className="hifz-card flex items-start gap-4" data-testid="basic-ai-allowance">
        <div className="h-10 w-10 rounded-xl bg-forest-600/12 text-forest-700 grid place-items-center shrink-0"><Brain className="h-5 w-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-forest-700">{t("basicHifz.aiTitle")}</div>
          <p className="text-sm text-foreground/75 mt-1">{t("basicHifz.aiBody", { minutes: data.ai_test_minutes })}</p>
          <Link to="/app/hifz-test" className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-forest-700 hover:underline" data-testid="basic-test-cta">
            {t("basicHifz.testCta")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {data.ladder.some((st) => !st.open) && (
        <section className="hifz-card border-gold-500/40 bg-gold-500/5 flex items-center gap-3" data-testid="basic-upgrade">
          <Crown className="h-5 w-5 text-gold-600 shrink-0" />
          <div className="flex-1 text-sm text-foreground/80">
            <strong className="text-forest-700">{t("basicHifz.upgradeTitle")}</strong> {t("basicHifz.upgradeBody")}
          </div>
          <button type="button" onClick={goToPlans} className="btn-accent text-xs shrink-0" data-testid="basic-see-plans">{t("basicHifz.seePlans")}</button>
        </section>
      )}
    </div>
  );
}
